// Diagnóstico AO VIVO, espelhando o validate-wordsearch-draft.ts do servidor.
//
// O servidor continua sendo a autoridade (o save/preview devolve 422 com os erros
// dele). Isto aqui existe para o autor não descobrir o problema só ao salvar.

import type { Diagnostic } from '../../types';
import { cellIndex, lineCells, type WordsearchDoc } from './doc';

export function validate(doc: WordsearchDoc): Diagnostic[] {
  const out: Diagnostic[] = [];

  const isLetterCell = (l: string) => /^[A-Z]$/.test(l);
  if (doc.letters.some((l) => !isLetterCell(l))) {
    out.push({
      level: 'error',
      message: 'A grade tem célula vazia ou inválida (cada célula precisa ser exatamente uma letra A–Z). Preencha todas antes de salvar.',
    });
  }

  if (doc.words.length === 0) {
    out.push({ level: 'error', message: 'Nenhuma palavra colocada na grade.' });
  }

  const seenRefs = new Set<string>();
  const seenSolutions = new Set<string>();

  for (const w of doc.words) {
    if (seenRefs.has(w.ref)) {
      out.push({ level: 'error', message: `Palavra com ref repetido: ${w.ref}.`, ref: w.ref });
      continue;
    }
    seenRefs.add(w.ref);

    if (w.clue.trim() === '') {
      out.push({ level: 'error', message: `A palavra ${w.solution} está sem dica.`, ref: w.ref });
    }

    const cells = lineCells(w);

    if (cells.length === 0) {
      out.push({
        level: 'error',
        message: `${w.solution}: a reta precisa ser horizontal, vertical ou diagonal.`,
        ref: w.ref,
      });
      continue;
    }

    // O servidor aceita diagonal em qualquer sentido, mas exige H da esquerda pra
    // direita e V de cima pra baixo (validate-wordsearch-draft.ts). O `lineCells`
    // ignora o sinal de propósito (o Canvas usa pra desenhar o arrasto ao contrário
    // também), então quem barra o sentido errado é aqui.
    const horizontalBackwards = w.y1 === w.y2 && w.x2 < w.x1;
    const verticalBackwards = w.x1 === w.x2 && w.y2 < w.y1;
    if (horizontalBackwards || verticalBackwards) {
      out.push({
        level: 'error',
        message: `${w.solution}: palavra horizontal tem que ir da esquerda para a direita, e vertical de cima para baixo. Arraste no outro sentido.`,
        ref: w.ref,
        x: w.x1,
        y: w.y1,
      });
      continue;
    }

    if (w.x1 === w.x2 && w.y1 === w.y2) {
      out.push({
        level: 'error',
        message: `${w.solution}: a palavra precisa ocupar pelo menos duas células.`,
        ref: w.ref,
        x: w.x1,
        y: w.y1,
      });
      continue;
    }

    const outOfBounds = cells.some((c) => c.x < 1 || c.y < 1 || c.x > doc.width || c.y > doc.height);
    if (outOfBounds) {
      out.push({
        level: 'error',
        message: `${w.solution} sai dos limites da grade.`,
        ref: w.ref,
        x: w.x1,
        y: w.y1,
      });
      continue;
    }

    if (cells.length !== w.solution.length) {
      out.push({
        level: 'error',
        message: `${w.solution}: o comprimento da reta (${cells.length}) não bate com o da palavra (${w.solution.length}).`,
        ref: w.ref,
      });
      continue;
    }

    const gridWord = cells.map((c) => doc.letters[cellIndex(c.x, c.y, doc.width)]).join('');
    if (gridWord !== w.solution) {
      out.push({
        level: 'error',
        message: `${w.solution} não confere com a grade (a grade diz "${gridWord}").`,
        ref: w.ref,
        x: w.x1,
        y: w.y1,
      });
    }

    if (seenSolutions.has(w.solution)) {
      out.push({
        level: 'warning',
        message: `A palavra ${w.solution} está repetida.`,
        ref: w.ref,
      });
    }
    seenSolutions.add(w.solution);
  }

  return out;
}
